// React and Next.js core imports
import { useTranslation } from "@/hooks/useTranslation.hook";
import React from "react";

// Types
interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Global Error Boundary Component
const GlobalErrorBoundary: React.FC<Props> = ({ children }) => {
  const { t } = useTranslation();
  const [state, setState] = React.useState<State>({
    hasError: false,
    error: null,
  });

  React.useEffect(() => {
    // Initialize error tracking
    const handleError = (error: ErrorEvent) => {
      // Ignore null/undefined errors and errors from known sources
      if (!error.error) {
        console.warn("Global error boundary: Ignoring null/undefined error");
        return;
      }

      // Ignore errors that are marked as handled (e.g., upload errors)
      if ((error.error as any)?.isUploadError) {
        console.warn("Global error boundary: Ignoring handled upload error");
        return;
      }

      // Ignore errors from SunEditor or image upload related
      const errorMessage = error.error?.message || String(error.error);
      if (
        errorMessage.includes("Image upload") ||
        errorMessage.includes("upload") ||
        errorMessage.includes("SunEditor") ||
        error.filename?.includes("text-editor")
      ) {
        console.warn(
          "Global error boundary: Ignoring image upload/SunEditor error"
        );
        return;
      }

      setState({ hasError: true, error: error.error });
      console.error("Global error boundary caught error:", error.error);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Prevent default to avoid showing in console if we're handling it
      event.preventDefault();

      // Ignore null/undefined rejections
      if (event.reason === null || event.reason === undefined) {
        console.warn(
          "Global error boundary: Ignoring null/undefined rejection"
        );
        return;
      }

      // Ignore rejections that are marked as handled
      if ((event.reason as any)?.isUploadError) {
        console.warn(
          "Global error boundary: Ignoring handled upload rejection"
        );
        return;
      }

      // Ignore upload-related rejections
      const errorMessage =
        event.reason instanceof Error
          ? event.reason.message
          : String(event.reason);
      if (
        errorMessage.includes("Image upload") ||
        errorMessage.includes("upload") ||
        errorMessage.includes("SunEditor")
      ) {
        console.warn("Global error boundary: Ignoring image upload rejection");
        return;
      }

      setState({ hasError: true, error: event.reason });
      console.error(
        "Global error boundary caught unhandled rejection:",
        event.reason
      );
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
    };
  }, []);

  if (state.hasError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-4 bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-lg">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">
              {t("Oops! Something went wrong")}
            </h2>
            <p className="text-gray-600 mb-6">
              {t(
                "We apologize for the inconvenience Our team has been notified and is working to fix the issue"
              )}
            </p>
            <div className="space-y-4">
              <button
                onClick={() => {
                  setState({ hasError: false, error: null });
                  window.location.reload();
                }}
                className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                {t("Try again")}
              </button>
              <button
                onClick={() => (window.location.href = "/")}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
              >
                {t("Go to Homepage")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default GlobalErrorBoundary;
